import DemoNextSteps from '@/components/DemoNextSteps';
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useDemoRequestForm } from '@/hooks/demo-req-form';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Phone, User, MessageSquare, Sparkles, CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import DemoRequestDialog from '@/components/DemoRequestDialog';
import Turnstile from '@/components/Turnstile';
import { trackEvent } from '@/lib/analytics';
import Seo from '@/components/Seo';

function DemoRequest() {
  const [isDemoDialogOpen, setIsDemoDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    description: '',
  });
  const [errors, setErrors] = useState({});
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [captchaCycle, setCaptchaCycle] = useState(0);
  const { submitDemoRequestForm, loading, error, success } = useDemoRequestForm();
  const startedRef = useRef(false);
  const formRef = useRef(null);
  const viewedRef = useRef(false);
  const trackStep = (step, data = {}) => trackEvent('lead-form-' + step, { form: 'demo', ...data });
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !viewedRef.current) {
          viewedRef.current = true;
          trackEvent('lead-form-view', { form: 'demo' });
        }
      },
      { threshold: 0.1 }
    );
    if (formRef.current) observer.observe(formRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (!startedRef.current) {
      startedRef.current = true;
      trackStep('start');
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'İsim alanı zorunludur';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefon numarası zorunludur';
    }

    if (formData.phone && !/^[0-9\s+()-]+$/.test(formData.phone)) {
      newErrors.phone = 'Geçerli bir telefon numarası giriniz';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    trackStep('attempt');

    if (!validateForm()) {
      trackStep('error', { reason: 'validation' });
      return;
    }

    if (!turnstileToken) {
      trackStep('error', { reason: 'captcha_missing' });
      setErrors({ submit: 'Doğrulama tamamlanamadı. Lütfen sayfayı yenileyip tekrar deneyiniz.' });
      return;
    }

    try {
      const result = await submitDemoRequestForm({
        name: formData.name,
        phone: formData.phone,
        message: formData.description,
        recaptchaToken: turnstileToken,
      });
      setTurnstileToken(null);
      setCaptchaCycle((cycle) => cycle + 1);

      if (result.success) {
        trackEvent('demo-request-submit', { source: 'page' });
        trackStep('success');
        setFormData({ name: '', phone: '', description: '' });
      } else {
        trackStep('error', { reason: 'request_failed' });
      }
    } catch {
      trackStep('error', { reason: 'unexpected' });
      setErrors({ submit: 'Bir hata oluştu. Lütfen tekrar deneyiniz.' });
    }
  };

  return (
    <div className="App">
      <Seo
        title="Ücretsiz Demo Talebi - Tilki Otomasyon"
        description="Tilki Otomasyon'u ücretsiz deneyin. Adınızı ve telefonunuzu bırakın. Sizi arayıp tanıtım için birlikte zaman belirleyelim."
        path="/demo"
      />
      <Header onOpenDemoRequestDialog={setIsDemoDialogOpen} />

      {/* Hero Section */}
      <main id="main-content">
        <section className="relative pt-10 pb-14 lg:pt-14 lg:pb-20 bg-surface overflow-hidden">
          <div className="brand-glow w-[480px] h-[480px] -top-32 left-1/2 -translate-x-1/2" aria-hidden="true" />
          <div className="absolute inset-0 bg-grid-pattern opacity-50" aria-hidden="true" />

          <div className="container relative mx-auto px-4">
            <div className="text-center mb-12">
              <span className="section-eyebrow mb-4">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                Ücretsiz Demo
              </span>
              <h1 className="type-h2 text-balance mt-4 mb-3">
                Tilki’yi birlikte <span className="text-gradient">inceleyelim</span>
              </h1>
              <p className="type-body-lg text-pretty max-w-2xl mx-auto">
                İşletmeniz için özel bir demo hazırlayalım. Formu doldurun, biz sizi arayalım.
              </p>
            </div>

            {/* Form Section */}
            <div className="max-w-3xl mx-auto">
              {success ? (
                <Card className="border border-hairline shadow-card-lift">
                  <CardContent className="p-12">
                    <div className="text-center" role="status">
                      <div className="mx-auto w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mb-6">
                        <CheckCircle className="w-8 h-8" aria-hidden="true" />
                      </div>
                      <h2 className="font-poppins font-bold text-ink text-3xl tracking-tight mb-3">
                        Talebiniz Alındı!
                      </h2>
                      <p className="type-body-lg text-pretty mb-6">
                        Bıraktığınız telefon numarasından size ulaşıp tanıtım zamanını birlikte belirleyeceğiz.
                      </p>
                      <Link to="/" className="modern-btn-outline">
                        Ana sayfaya dön
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border border-hairline shadow-card-lift overflow-hidden">
                  {/* Card Header */}
                  <div className="relative bg-ink-strong text-white p-8 overflow-hidden">
                    <div className="brand-glow w-72 h-72 -top-20 -right-20 opacity-40" aria-hidden="true" />
                    <div className="relative flex items-center space-x-3">
                      <div className="bg-white/10 border border-white/15 backdrop-blur-sm p-3 rounded-xl">
                        <Sparkles className="w-6 h-6" aria-hidden="true" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold font-poppins tracking-tight text-white">Demo Talep Formu</h2>
                        <p className="text-white/70 text-sm mt-1 font-inter">Adınız ve telefonunuz yeterli</p>
                      </div>
                    </div>
                  </div>

                  {/* Form */}
                  <CardContent className="p-8">
                    <DemoNextSteps />
                    <form
                      ref={formRef}
                      onSubmit={handleSubmit}
                      onInvalidCapture={() => trackStep('error', { reason: 'validation' })}
                      className="space-y-6"
                    >
                      {/* Name Field */}
                      <div>
                        <label htmlFor="demo-name" className="block text-sm font-medium text-ink mb-2 font-inter">
                          <User className="w-4 h-4 inline mr-1" aria-hidden="true" />
                          Ad Soyad <span aria-hidden="true">*</span>
                          <span className="sr-only">(zorunlu)</span>
                        </label>
                        <input
                          id="demo-name"
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          autoComplete="name"
                          required
                          aria-invalid={errors.name ? 'true' : 'false'}
                          aria-describedby={errors.name ? 'demo-name-error' : undefined}
                          className={`w-full px-4 py-3 rounded-xl border ${
                            errors.name ? 'border-red-500' : 'border-hairline'
                          } focus-ring-modern transition-all font-inter`}
                          placeholder="Adınız ve soyadınız"
                        />
                        {errors.name && (
                          <p id="demo-name-error" className="text-red-500 text-sm mt-1 font-inter">
                            {errors.name}
                          </p>
                        )}
                      </div>

                      {/* Phone Field */}
                      <div className="grid grid-cols-1 gap-6">
                        <div>
                          <label htmlFor="demo-phone" className="block text-sm font-medium text-ink mb-2 font-inter">
                            <Phone className="w-4 h-4 inline mr-1" aria-hidden="true" />
                            Telefon <span aria-hidden="true">*</span>
                            <span className="sr-only">(zorunlu)</span>
                          </label>
                          <input
                            id="demo-phone"
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            autoComplete="tel"
                            aria-invalid={errors.phone ? 'true' : 'false'}
                            aria-describedby={errors.phone ? 'demo-phone-error' : 'demo-channel-hint'}
                            className={`w-full px-4 py-3 rounded-xl border ${
                              errors.phone ? 'border-red-500' : 'border-hairline'
                            } focus-ring-modern transition-all font-inter`}
                            placeholder="+90 5XX XXX XX XX"
                          />
                          {errors.phone && (
                            <p id="demo-phone-error" className="text-red-500 text-sm mt-1 font-inter">
                              {errors.phone}
                            </p>
                          )}
                        </div>
                      </div>

                      <details className="rounded-xl border border-hairline p-4">
                        <summary className="cursor-pointer text-sm font-medium text-ink">
                          Mesaj ekle (isteğe bağlı)
                        </summary>
                        <div className="mt-4">
                          {/* Açıklama (opsiyonel) */}
                          <div>
                            <label
                              htmlFor="demo-description"
                              className="block text-sm font-medium text-ink mb-2 font-inter"
                            >
                              <MessageSquare className="w-4 h-4 inline mr-1" aria-hidden="true" />
                              Açıklama
                            </label>
                            <textarea
                              id="demo-description"
                              name="description"
                              value={formData.description}
                              onChange={handleChange}
                              rows={4}
                              className="w-full px-4 py-3 rounded-xl border border-hairline focus-ring-modern transition-all font-inter resize-y"
                              placeholder="İşletmeniz ve ihtiyaçlarınız hakkında kısaca bilgi verin (opsiyonel)"
                            />
                          </div>
                        </div>
                      </details>

                      <div className="w-full">
                        <Turnstile
                          key={captchaCycle}
                          onVerify={(token) => {
                            setTurnstileToken(token);
                            setErrors((prev) => ({ ...prev, submit: '' }));
                          }}
                          onError={() => {
                            setTurnstileToken(null);
                            trackStep('error', { reason: 'captcha_load' });
                            setErrors((prev) => ({
                              ...prev,
                              submit: 'Doğrulama yüklenemedi. Lütfen sayfayı yenileyiniz.',
                            }));
                          }}
                          onExpire={() => setTurnstileToken(null)}
                        />
                      </div>

                      {/* Error Messages */}
                      <div aria-live="polite" aria-atomic="true" className="space-y-4 empty:hidden">
                        {error && (
                          <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <p className="text-red-600 text-sm font-inter">{error}</p>
                          </div>
                        )}

                        {errors.submit && (
                          <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <p className="text-red-600 text-sm font-inter">{errors.submit}</p>
                          </div>
                        )}
                      </div>

                      {/* Note */}
                      <p id="demo-channel-hint" className="text-xs text-ink-muted font-inter">
                        Bu form bir tanıtım talebidir; abonelik veya ödeme başlatmaz.
                      </p>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={loading}
                        aria-busy={loading}
                        className="modern-btn-large w-full text-lg space-x-2 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                            <span>Gönderiliyor...</span>
                          </>
                        ) : (
                          <>
                            <span>Tanıtım talebimi gönder</span>
                            <ArrowRight className="w-5 h-5" aria-hidden="true" />
                          </>
                        )}
                      </button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Demo Request Dialog */}
      <DemoRequestDialog isOpen={isDemoDialogOpen} onClose={() => setIsDemoDialogOpen(false)} />
    </div>
  );
}

export default DemoRequest;
